import { unref } from "vue";
import AXIOS, { type AxiosResponse } from "axios";
import { useStore } from "@/stores";
import type { HttpResult } from "@/global";
import router from "@/router";
import { getToken, getUuid, removeToken } from "./auth";
import { error as errorMsg, success as successMsg } from "./message";
import { fromBase64, toBase64 } from "./string";

const axios = AXIOS.create({
  withCredentials: false, // send cookies when cross-domain requests
  timeout: 60000, // request timeout
});

// request interceptor
axios.interceptors.request.use(
  (request) => {
    // do something before request is sent
    const token = getToken();
    if (token && request.headers) {
      request.headers["token"] = token;
    }

    const uuid = getUuid();
    if (uuid && request.headers) {
      request.headers["uuid"] = uuid;
    }

    return request;
  },
  (error) => {
    // do something with request error
    console.log(error); // for debug
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  function (response) {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    //return response.data;
    if (response.status === 200 && response.data === "expired") {
      removeToken();
      router.replace({ path: "/" });
    }

    return response;
  },
  async function (error) {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // Do something with response error
    console.log(error); // for debug
    errorMsg(error.response.data || error.message);

    return Promise.reject(error);
  }
);

export const $http = axios;

export const post = (
  url: string,
  data?: Record<string, any>
): Promise<{ success: boolean; data: any }> => {
  const network: {
    时间: string;
    请求方法: string;
    请求载荷: Record<string, any> | undefined;
    [key: string]: any;
  } = {
    时间: new Date().toLocaleString(),
    请求方法: url,
    请求载荷: unref(data),
  };

  const { networkVisible } = useStore();
  if (networkVisible) {
    console.log(network);
  }

  return axios
    .post(
      `/api/${url}`,
      data ? { args: encodeURIComponent(toBase64(JSON.stringify(data))) } : {}
    )
    .then((result: AxiosResponse<{ args: string }, any>) => {
      const data: HttpResult = JSON.parse(
        fromBase64(decodeURIComponent(result.data.args))
      );
      network["响应"] = data;
      if (!data.success) {
        errorMsg(data.message);
        return { success: false, data: undefined };
      }

      if (data.message) {
        successMsg(data.message);
      }

      return { success: true, data: data.data };
    })
    .catch((error) => {
      if (error.response.status === 401) {
        return { success: false, data: 401 };
      }

      return { success: false, data: undefined };
    });
};

export const postFormData = (
  url: string,
  data: FormData
): Promise<{ success: boolean; data: any }> => {
  return axios
    .post(url, data)
    .then((resp: AxiosResponse<HttpResult, any>) => {
      const data: HttpResult = resp.data;
      if (!data.success) {
        errorMsg(data.message);
        return { success: false, data: undefined };
      }

      if (data.message) {
        successMsg(data.message);
      }

      return { success: true, data: data.data };
    })
    .catch(() => {
      return { success: false, data: undefined };
    });
};
